import { useSearchParams } from 'react-router-dom'
import { observer } from 'mobx-react'
import { Box, Button, Fade, IconButton, Slide, Typography, useTheme } from '@mui/material'

import limitsReachedImg from '../../../assets/limits-reached.svg'
import sadSimba from '../../../assets/simba-ai/sad.png'
import { useSimbaAIStore } from '../../../store/simbaai.store'
import DataLoadingDots from '../../common/DataLoadingDots'
import AIWaitingScreen from '../AIWaitingScreen'
import SubjectTopics from './view/SubjectTopics'
import LearnModeHome from './view/LearnModeHome'
import { useSettingsStore } from '../../../store/settings.store'
import AiResponse from './view/AiResponse'
import { TransitionGroup } from 'react-transition-group'
import { Icons, markdownToText } from '../../../utils/utilities'
import { useRef, useState } from 'react'
import CustomMarkdown from '../CustomMarkdown'

const getUniqueKeyForView = view => {
    switch ( view ) {
        case "loading":
            return 3

        case "limits-reached":
            return 2

        case "simba-ai-response":
        case "fetching-simba-ai-response":
        case "completed-typing-simba-ai-response":
            return 1

        default:
            return 0
    }
}

const Renderer = observer( ( { closeAiMentorDialog } ) => {

    const [listeningPaused, setListeningPaused] = useState( false )
    const [listening, setListening] = useState( false )

    const { layout, border, palette } = useTheme()

    const SimbaAIStore = useSimbaAIStore()
    const SettingsStore = useSettingsStore()

    const aiMentorArea = useRef()

    const setSearchParams = useSearchParams().at( 1 )

    const setSearchParamsValues = ( values ) => {
        setSearchParams( prev => {
            const newParams = new URLSearchParams( prev )
            for ( const key in values ) {
                if ( !Object.hasOwn( values, key ) ) continue
                if ( values[key] )
                    newParams.set( key, values[key] )
                else
                    newParams.delete( key )
            }
            return newParams
        }, { replace: true } )
    }

    const retryForAiResponse = async () => {
        if ( SimbaAIStore.getAiResponsesQueue.request ) {
            SimbaAIStore.addResponse( { topic: SimbaAIStore.getAiResponsesQueue.request, response: SimbaAIStore.getAiResponsesQueue.response } )
            SimbaAIStore.setAiResponsesQueue( { request: "", response: "" } )
        }
        try {
            SimbaAIStore.setView( 'fetching-simba-ai-response', "left", false )
            if ( await SimbaAIStore.getAIResponse( SimbaAIStore.getCurrentTopic ) )
                SimbaAIStore.setView( 'simba-ai-response', "left", false )
            else
                SimbaAIStore.setView( 'simba-ai-response-error', "left", false )

        } catch ( err ) {
            console.log( err )
        }
    }

    const backToHome = () => {
        SimbaAIStore.setView( "home", "right" )
        setSearchParamsValues( { simbaAIOpen: 'false', practiceQuiz: "", simbaAIMode: "" } )
    }


    const listenToResponse = () => {
        if ( !listening ) {
            if ( speechSynthesis.speaking )
                speechSynthesis.cancel()
            const speechText = markdownToText( SimbaAIStore.getAiResponse )
            const utterance = new SpeechSynthesisUtterance( speechText )
            utterance.onpause = () => setListeningPaused( true )
            utterance.onresume = () => setListeningPaused( false )
            utterance.onend = () => setListening( false )
            utterance.onerror = () => setListening( false )
            const voices = speechSynthesis.getVoices()
            utterance.voice = voices[0]
            speechSynthesis.speak( utterance )
            setListening( true )
        }
    }

    const toggleListening = () => {
        if ( !speechSynthesis.paused ) {
            speechSynthesis.pause()
        } else {
            speechSynthesis.resume()
        }
    }

    const stopListening = () => {
        speechSynthesis.cancel()
        setListening( false )
    }


    switch ( SimbaAIStore.getView.current ) {
        case "loading":
        case "fetching-simba-ai-response":
            return <AIWaitingScreen />

        case "simba-ai-response-error":
            return (
                <Box padding="20px">
                    <Box position="relative" border={`2px dashed ${palette.borderColor}`} borderRadius="20px" flexGrow={1} display="flex" flexDirection="column" alignItems="center" padding="20px" gap="10px" justifyContent="center" >
                        <Box position="absolute" height="60%" maxHeight="400px" bgcolor={palette.contentBg} borderRadius="50%" sx={{ aspectRatio: "1 / 1", zIndex: 1 }} />
                        <Box position="relative" sx={{ zIndex: 10 }} component="img" src={sadSimba} height="180px" alt="No practice sessions" />
                        <Typography position="relative" sx={{ zIndex: 10 }} textAlign="center" variant="subtitle2" component="p" >{SimbaAIStore.getErrors.aiResponse || "Something went wrong while trying to generate your response"}</Typography>
                        <Box display="flex" gap={layout.cardsGap} alignItems="center" flexWrap="wrap">
                            <Button className='animation-wave' onClick={retryForAiResponse} sx={{ borderBottom: `4px solid ${palette.secondary.light + "99"}`, borderRadius: "10px" }} startIcon={Icons.default.Replay} variant='contained' disableElevation color='secondary' >Retry</Button>
                            <Button onClick={backToHome} sx={{ borderBottom: `4px solid ${palette.error.light + "99"}`, borderRadius: "10px" }} startIcon={Icons.default.Logout} variant='contained' disableElevation color='error' >Exit</Button>
                        </Box>
                    </Box>
                </Box>
            )

        case "limits-reached":
            return ( <Box flexGrow={1} display="flex" gap="20px" alignItems="center" flexDirection="column" padding="20px" justifyContent="center">
                <Box component="img" alt='daily-limits-reached' src={limitsReachedImg} width="200px" />
                <Typography variant='subtitle2'>You reached the limit for the day! You can only make upto {SettingsStore.getUseSettings['aitutor_max_requests_per_day']} requests per day.</Typography>
                <Button variant='contained' color='secondary' sx={{ textTransform: "capitalize" }} disableElevation onClick={closeAiMentorDialog} >Okay</Button>
            </Box> )

        case "simba-ai-response":
        case "completed-typing-simba-ai-response":
            return (
                <Box overflow="auto" display="flex" flexDirection="column" padding={layout.pagePadding} gap={layout.cardsGap} flexGrow={1}>
                    {SimbaAIStore.getView.current === "fetching-simba-ai-response" && <DataLoadingDots padding="20px" waitingMessage='Please wait Simba is generating your response...' />}
                    {SimbaAIStore.getView.current === "fetching-simba-ai-response" && <Box flexGrow={1}> </Box>}
                    {["completed-typing-simba-ai-response", "simba-ai-response"].includes( SimbaAIStore.getView.current ) && <Box flexGrow={1} display="flex" flexDirection="column" overflow="auto" borderRadius={layout.borderRadius} border={border[1]} >
                        <Box padding="10px" bgcolor={palette.contentBg} display="flex" gap={layout.cardsGap} flexWrap="wrap" justifyContent="space-between" alignItems="center" borderBottom={border[1]}>
                            <Box>
                                <Typography variant="h6" component="h3" fontSize="16px" fontWeight="200">Response for the request <strong>Explain {SimbaAIStore.getCurrentTopic?.topic}</strong></Typography>
                                <Typography variant="subtitle2" marginTop="-5px" color="GrayText">From: <em>{SimbaAIStore.getCurrentTopic?.subject_name}, {SimbaAIStore.getCurrentTopic?.module}</em></Typography>
                            </Box>
                            <Box display="flex" padding="10px 20px" borderRadius="10px" bgcolor={palette.secondary.main} gap="10px" alignItems="center" justifyContent="space-between">
                                <Box color={palette.secondary.contrastText} onClick={listenToResponse} display="flex" gap="10px" alignItems="center">
                                    {listening ? Icons.small.GraphicEqRounded : Icons.small.VolumeUpIcon}
                                    <Typography variant="subtitle2" color={palette.secondary.contrastText} >{listening ? "Listening" : "Listen"}</Typography>
                                </Box>
                                {listening && <IconButton onClick={toggleListening} sx={{ color: palette.secondary.contrastText }} size="small">{listeningPaused ? Icons.default.PlayArrowIcon : Icons.default.PauseIcon}</IconButton>}
                                {listening && <IconButton onClick={stopListening} sx={{ color: palette.secondary.contrastText }} size="small">{Icons.default.Stop}</IconButton>}
                            </Box>
                        </Box>
                        <Box flexGrow={1} ref={aiMentorArea} className="slim-custom-scrollbar" padding="20px" overflow="auto" >
                            <CustomMarkdown scrollToBottom scrollContainer={aiMentorArea} typeWriterEffect content={SimbaAIStore.getAiResponse || ""} speed={80} />
                        </Box>
                    </Box>}
                    <Box display="flex" borderRadius={layout.borderRadius} flexDirection="column" gap="10px">
                        <Button className='animation-wave' onClick={backToHome} sx={{ borderBottom: `4px solid ${palette.error.light + "99"}`, borderRadius: "10px" }} startIcon={Icons.default.Logout} variant='contained' disableElevation color='error' >Exit</Button>
                    </Box>
                </Box>
            )

        default:
            return null
    }
} )

const SampleLearnModeViewRendererForFaculty = observer( ( { closeAiMentorDialog } ) => {

    const SimbaAIStore = useSimbaAIStore()



    return (
        <Box flexGrow={1} display="flex" flexDirection="column" overflow="auto" position="relative" gap="10px" >

            <TransitionGroup style={{ minHeight: 0, height: "100%", display: "flex", flexDirection: "column", overflow: "auto" }}>
                <Fade
                    key={getUniqueKeyForView( SimbaAIStore.getView.current )}
                    timeout={{ enter: 100, exit: 0 }}
                >
                    <Slide
                        in
                        direction={SimbaAIStore.getView.slideDirection || "left"}
                        timeout={{ enter: 350, exit: 0 }}
                    >
                        <Box minHeight={0} flexGrow={1} overflow="auto" display="flex" flexDirection="column">
                            <Renderer closeAiMentorDialog={closeAiMentorDialog} />
                        </Box>
                    </Slide>
                </Fade>
            </TransitionGroup>
        </Box>
    )

} )

export default SampleLearnModeViewRendererForFaculty